import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { 
  Upload, Download, FileImage, FileText, AlertTriangle, 
  Shield, Check, X, Eye, EyeOff, Zap, Info
} from 'lucide-react';
import { PDFDocument } from 'pdf-lib';

const MetadataRemover = () => {
  const [file, setFile] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cleanedFile, setCleanedFile] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const supportedTypes = {
    'image/jpeg': { ext: 'JPG/JPEG', icon: FileImage },
    'image/png': { ext: 'PNG', icon: FileImage },
    'image/webp': { ext: 'WEBP', icon: FileImage },
    'application/pdf': { ext: 'PDF', icon: FileText },
  };

  const extractImageMetadata = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const meta = {
            'File Name': file.name,
            'File Size': (file.size / 1024).toFixed(2) + ' KB',
            'File Type': file.type,
            'Last Modified': new Date(file.lastModified).toLocaleString(),
            'Image Width': img.width + 'px',
            'Image Height': img.height + 'px',
            'Aspect Ratio': (img.width / img.height).toFixed(2),
          };
          resolve(meta);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const extractPDFMetadata = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    
    const meta = {
      'File Name': file.name,
      'File Size': (file.size / 1024).toFixed(2) + ' KB',
      'File Type': file.type,
      'Last Modified': new Date(file.lastModified).toLocaleString(),
      'Page Count': pdfDoc.getPageCount(),
    };

    // Extract PDF metadata
    const title = pdfDoc.getTitle();
    const author = pdfDoc.getAuthor();
    const subject = pdfDoc.getSubject();
    const creator = pdfDoc.getCreator();
    const producer = pdfDoc.getProducer();
    const keywords = pdfDoc.getKeywords();
    const creationDate = pdfDoc.getCreationDate();
    const modificationDate = pdfDoc.getModificationDate();

    if (title) meta['Title'] = title;
    if (author) meta['Author'] = author;
    if (subject) meta['Subject'] = subject;
    if (creator) meta['Creator'] = creator;
    if (producer) meta['Producer'] = producer;
    if (keywords) meta['Keywords'] = keywords;
    if (creationDate) meta['Creation Date'] = creationDate.toLocaleString();
    if (modificationDate) meta['Modification Date'] = modificationDate.toLocaleString();

    return meta;
  };

  const cleanImageMetadata = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          
          canvas.toBlob((blob) => {
            const cleanFile = new File(
              [blob], 
              file.name.replace(/\.[^/.]+$/, '_cleaned.png'),
              { type: 'image/png' }
            );
            resolve(cleanFile);
          }, 'image/png', 1.0);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const cleanPDFMetadata = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    
    // Remove all metadata
    pdfDoc.setTitle('');
    pdfDoc.setAuthor('');
    pdfDoc.setSubject('');
    pdfDoc.setKeywords([]);
    pdfDoc.setProducer('');
    pdfDoc.setCreator('');
    
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const cleanFile = new File(
      [blob],
      file.name.replace(/\.pdf$/i, '_cleaned.pdf'),
      { type: 'application/pdf' }
    );
    
    return cleanFile;
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!supportedTypes[selectedFile.type]) {
      toast.error('Unsupported file type. Please upload JPG, PNG, WEBP, or PDF files.');
      return;
    }

    setFile(selectedFile);
    setCleanedFile(null);
    setLoading(true);
    setProgress(20);

    try {
      let extractedMetadata;
      
      if (selectedFile.type.startsWith('image/')) {
        extractedMetadata = await extractImageMetadata(selectedFile);
      } else if (selectedFile.type === 'application/pdf') {
        extractedMetadata = await extractPDFMetadata(selectedFile);
      }
      
      setProgress(100);
      setMetadata(extractedMetadata);
      toast.success('Metadata extracted successfully!');
    } catch (error) {
      console.error('Error extracting metadata:', error);
      toast.error('Failed to extract metadata');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanMetadata = async () => {
    if (!file) return;
    
    setLoading(true);
    setProgress(0);

    try {
      setProgress(30);
      let cleaned;
      
      if (file.type.startsWith('image/')) {
        cleaned = await cleanImageMetadata(file);
      } else if (file.type === 'application/pdf') {
        cleaned = await cleanPDFMetadata(file);
      }
      
      setProgress(100);
      setCleanedFile(cleaned);
      toast.success('Metadata removed successfully!');
    } catch (error) {
      console.error('Error cleaning metadata:', error);
      toast.error('Failed to clean metadata');
    } finally {
      setLoading(false);
    }
  };

  const downloadCleanedFile = () => {
    if (!cleanedFile) return;
    
    const url = URL.createObjectURL(cleanedFile);
    const link = document.createElement('a');
    link.href = url;
    link.download = cleanedFile.name;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('File downloaded!');
  };

  const reset = () => {
    setFile(null);
    setMetadata(null);
    setCleanedFile(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sensitiveKeys = ['Author', 'Creator', 'Producer', 'Keywords', 'Title', 'Subject'];

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
          Metadata Remover
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          Strip all identifying metadata from images and PDFs to protect your privacy. All processing happens locally in your browser.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: Upload & Extract */}
        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Upload className="w-5 h-5 text-primary" />
              Upload & Analyze
            </CardTitle>
            <CardDescription>Upload a file to view and remove its metadata</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!file ? (
              <div 
                className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Upload className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">Click to upload or drag & drop</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Supports JPG, PNG, WEBP, PDF
                    </p>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3">
                    {file.type.startsWith('image/') ? (
                      <FileImage className="w-8 h-8 text-primary flex-shrink-0" />
                    ) : (
                      <FileText className="w-8 h-8 text-primary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={reset}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {loading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Processing...</span>
                      <span>{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}

                {metadata && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Info className="w-4 h-4 text-primary" />
                        Detected Metadata
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMetadata(!showMetadata)}
                      >
                        {showMetadata ? (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Hide
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Show
                          </>
                        )}
                      </Button>
                    </div>

                    {showMetadata && (
                      <div className="bg-background/50 rounded-lg p-4 border border-border space-y-2 max-h-[300px] overflow-y-auto">
                        {Object.entries(metadata).map(([key, value]) => {
                          const isSensitive = sensitiveKeys.includes(key);
                          return (
                            <div key={key} className="flex justify-between text-sm py-1 border-b border-border/50 last:border-0">
                              <span className="font-medium flex items-center gap-2">
                                {isSensitive && <AlertTriangle className="w-3 h-3 text-orange-500" />}
                                {key}:
                              </span>
                              <span className="text-muted-foreground text-right ml-2">{value}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <Alert className="bg-orange-500/10 border-orange-500/20">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      <AlertDescription>
                        <strong>Privacy Warning:</strong> This file contains {Object.keys(metadata).length} metadata fields, 
                        including potentially sensitive information.
                      </AlertDescription>
                    </Alert>

                    {!cleanedFile && (
                      <Button
                        onClick={handleCleanMetadata}
                        disabled={loading}
                        className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
                      >
                        <Zap className="w-5 h-5 mr-2" />
                        Remove All Metadata
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: Result & Download */}
        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Shield className="w-5 h-5 text-secondary" />
              Cleaned Result
            </CardTitle>
            <CardDescription>Your file with metadata removed</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!cleanedFile ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4">
                <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                  <Shield className="w-10 h-10 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-lg">No cleaned file yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload a file and remove metadata to see the result here
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <Alert className="bg-green-500/10 border-green-500/20">
                  <Check className="w-4 h-4 text-green-500" />
                  <AlertDescription>
                    <strong>Success!</strong> All metadata has been removed from your file.
                  </AlertDescription>
                </Alert>

                <div className="bg-gradient-to-br from-card to-card/50 p-6 rounded-xl border border-border space-y-4">
                  <div className="flex items-start gap-3">
                    {file.type.startsWith('image/') ? (
                      <FileImage className="w-8 h-8 text-secondary flex-shrink-0" />
                    ) : (
                      <FileText className="w-8 h-8 text-secondary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cleanedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(cleanedFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="bg-background/50 rounded-lg p-3 border border-border">
                      <p className="text-muted-foreground mb-1">Original Size</p>
                      <p className="font-bold">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <div className="bg-background/50 rounded-lg p-3 border border-border">
                      <p className="text-muted-foreground mb-1">Cleaned Size</p>
                      <p className="font-bold">{(cleanedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={downloadCleanedFile}
                  className="w-full h-12 bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Cleaned File
                </Button>

                <Button
                  onClick={reset}
                  variant="outline"
                  className="w-full h-12"
                >
                  Clean Another File
                </Button>

                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Privacy Protected
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>✓ All EXIF data removed</li>
                    <li>✓ Location data stripped</li>
                    <li>✓ Device information cleared</li>
                    <li>✓ Author & creator data removed</li>
                    <li>✓ Timestamp metadata cleared</li>
                  </ul>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Info className="w-5 h-5 text-accent" />
            About Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h4 className="font-semibold">What is Metadata?</h4>
              <p className="text-sm text-muted-foreground">
                Metadata is hidden information embedded in files, including camera settings, 
                GPS location, author details, and timestamps.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Why Remove It?</h4>
              <p className="text-sm text-muted-foreground">
                Metadata can reveal sensitive information about you, your location, and your devices. 
                Removing it protects your privacy when sharing files.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">How It Works</h4>
              <p className="text-sm text-muted-foreground">
                All processing happens in your browser. Files are never uploaded to any server, 
                ensuring complete privacy and security.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supported Formats */}
      <Card className="border-border/50 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg">Supported Formats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(supportedTypes).map(([type, { ext, icon: Icon }]) => (
              <div key={type} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                <Icon className="w-6 h-6 text-primary" />
                <span className="font-medium">{ext}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MetadataRemover;
